import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postService } from '../../../services/postService';
import ToastNotification from '../../components/common/ToastNotification';
import DeleteConfirmation from '../../components/common/DeleteConfirmation';
import FormModal from '../../components/common/FormModal';

function PostContentBlocks() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    blockType: 'text',
    text: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchPostDetails();
    fetchContentBlocks();
  }, [postId]);

  const fetchPostDetails = async () => {
    try {
      const response = await postService.getById(postId);
      setPost(response.data);
    } catch (err) {
      setError('Failed to fetch post details');
    }
  };

  const fetchContentBlocks = async () => {
    try {
      const response = await postService.getPostContent(postId);
      const blocksArray = Array.isArray(response?.data) ? response.data :
                         Array.isArray(response?.data?.data) ? response.data.data : [];
      setBlocks(blocksArray);
    } catch (err) {
      setError('Failed to fetch content blocks');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleAdd = () => {
    setSelectedBlock(null);
    setFormData({
      blockType: 'text',
      text: '',
      image: '',
      order: blocks.length + 1
    });
    setShowFormModal(true);
  };

  const handleEdit = (block) => {
    setSelectedBlock(block);
    setFormData({
      blockType: block.blockType,
      text: block.text || '',
      image: block.image || '',
      order: block.order
    });
    setShowFormModal(true);
  };

  const handleDeleteClick = (block) => {
    setSelectedBlock(block);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    try {
      const blockData = {
        ...formData,
        postId: parseInt(postId)
      };

      if (selectedBlock) {
        await postService.updatePostContent(selectedBlock.id, blockData);
        showNotification('Content block updated successfully');
      } else {
        await postService.createPostContent(blockData);
        showNotification('Content block added successfully');
      }

      setShowFormModal(false);
      setSelectedBlock(null);
      await fetchContentBlocks();
    } catch (err) {
      showNotification('Failed to save content block', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await postService.deletePostContent(selectedBlock.id);
      showNotification('Content block deleted successfully');
      setShowDeleteModal(false);
      setSelectedBlock(null);
      await fetchContentBlocks();
    } catch (err) {
      showNotification('Failed to delete content block', 'danger');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getBlockPreview = (block) => {
    if (block.blockType === 'text') {
      return <div className="text-truncate" style={{ maxWidth: '300px' }}>{block.text}</div>;
    }
    if (block.blockType === 'image' && block.image) {
      return <img src={block.image} alt="Block" style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', borderRadius: '8px' }} />;
    }
    if (block.blockType === 'heading') {
      return <strong>{block.text}</strong>;
    }
    if (block.blockType === 'quote') {
      return <em>"{block.text?.substring(0, 50)}..."</em>;
    }
    if (block.blockType === 'youtube') {
      return <span>🎥 YouTube Video</span>;
    }
    return <span>{block.blockType}</span>;
  };

  if (loading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        <div className="admin-crud-card">
          <div className="admin-crud-card-header">
            <div>
              <button 
                className="admin-crud-btn admin-crud-btn-secondary"
                onClick={() => navigate('/admin/posts')}
                style={{ marginBottom: '12px' }}
              >
                ← Back to Posts
              </button>
              <h3 className="admin-crud-title">
                <span className="admin-crud-title-icon">📦</span>
                Content Blocks - {post?.title}
              </h3>
            </div>
            <button 
              className="admin-crud-btn admin-crud-btn-primary"
              onClick={handleAdd}
            >
              ➕ Add Block
            </button>
          </div>

          <div className="admin-crud-modal-body">
            {error && (
              <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
                <div className="admin-crud-toast-content admin-crud-toast-danger">
                  <div className="admin-crud-toast-header">
                    <strong>Error</strong>
                    <button onClick={() => setError(null)}>✕</button>
                  </div>
                  <div className="admin-crud-toast-body">{error}</div>
                </div>
              </div>
            )}

            {blocks.length > 0 ? (
              <div className="admin-crud-table-wrapper">
                <table className="admin-crud-table">
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>Order</th>
                      <th style={{ width: '100px' }}>Type</th>
                      <th>Content</th>
                      <th style={{ width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocks.map(block => (
                      <tr key={block.id}>
                        <td>{block.order}</td>
                        <td>
                          <span className={`admin-crud-badge admin-crud-badge-info`}>
                            {block.blockType}
                          </span>
                        </td>
                        <td>{getBlockPreview(block)}</td>
                        <td>
                          <div className="admin-crud-actions-group">
                            <button
                              className="admin-crud-action-btn admin-crud-action-edit"
                              onClick={() => handleEdit(block)}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="admin-crud-action-btn admin-crud-action-delete"
                              onClick={() => handleDeleteClick(block)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-crud-empty-state">
                <div className="admin-crud-empty-icon">📭</div>
                <div className="admin-crud-empty-text">No content blocks yet</div>
                <div className="admin-crud-empty-subtext">Click "Add Block" to create your first content block</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal for Add/Edit Block */}
      <FormModal
        show={showFormModal}
        onHide={() => {
          setShowFormModal(false);
          setSelectedBlock(null);
        }}
        title={selectedBlock ? 'Edit Content Block' : 'Add New Content Block'}
        size="lg"
      >
        <form className="admin-crud-form">
          <div className="admin-crud-form-group">
            <label className="admin-crud-label">Block Type</label>
            <select
              name="blockType"
              className="admin-crud-select"
              value={formData.blockType}
              onChange={handleChange}
            >
              <option value="text">Text</option>
              <option value="heading">Heading</option>
              <option value="image">Image</option>
              <option value="quote">Quote</option>
              <option value="list">List</option>
              <option value="code">Code</option>
              <option value="youtube">YouTube Video</option>
             <option value="html">Html Block</option>
            </select>
          </div>

          {/* Text Block */}
        {/* Text + Quote + HTML Block */}
        {(formData.blockType === 'text' ||
          formData.blockType === 'quote' ||
          formData.blockType === 'html') && (
          <div className="admin-crud-form-group">
            <label className="admin-crud-label">
              {formData.blockType === 'html' ? 'HTML Content' : 'Content'}
            </label>

            <textarea
              name="text"
              className="admin-crud-textarea"
              rows="8"
              value={formData.text}
              onChange={handleChange}
              placeholder={
                formData.blockType === 'quote'
                  ? "Enter quote text..."
                  : formData.blockType === 'html'
                  ? "Enter HTML like <b>Bold</b>, <h1>Title</h1>"
                  : "Enter your content here..."
              }
            />
          </div>
        )}

          {/* Heading Block */}
          {formData.blockType === 'heading' && (
            <>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">Heading Level</label>
                <select
                  name="image"
                  className="admin-crud-select"
                  value={formData.image || 'h2'}
                  onChange={handleChange}
                >
                  <option value="h1">H1 - Main Heading</option>
                  <option value="h2">H2 - Section Heading</option>
                  <option value="h3">H3 - Subheading</option>
                  <option value="h4">H4 - Small Heading</option>
                </select>
              </div>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">Heading Text</label>
                <input
                  type="text"
                  name="text"
                  className="admin-crud-input"
                  value={formData.text}
                  onChange={handleChange}
                  placeholder="Enter heading text"
                />
              </div>
            </>
          )}

          {/* Image Block */}
          {formData.blockType === 'image' && (
            <div className="admin-crud-form-group">
              <label className="admin-crud-label">Image URL</label>
              <input
                type="text"
                name="image"
                className="admin-crud-input"
                value={formData.image}
                onChange={handleChange}
                placeholder="https://cdn.jsdelivr.net/gh/username/repo@main/image.jpg"
              />
              {formData.image && (
                <div style={{ marginTop: '12px' }}>
                  <img src={formData.image} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                </div>
              )}
            </div>
          )}

          {/* YouTube Block */}
          {formData.blockType === 'youtube' && (
            <div className="admin-crud-form-group">
              <label className="admin-crud-label">YouTube URL</label>
              <input
                type="text"
                name="text"
                className="admin-crud-input"
                value={formData.text}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
          )}

          {/* List Block */}
          {formData.blockType === 'list' && (
            <>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">List Type</label>
                <select
                  name="image"
                  className="admin-crud-select"
                  value={formData.image || 'unordered'}
                  onChange={handleChange}
                >
                  <option value="unordered">Bullet List</option>
                  <option value="ordered">Numbered List</option>
                </select>
              </div>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">List Items (one per line)</label>
                <textarea
                  name="text"
                  className="admin-crud-textarea"
                  rows="5"
                  value={formData.text}
                  onChange={handleChange}
                  placeholder="Item 1&#10;Item 2&#10;Item 3"
                />
              </div>
            </>
          )}

          {/* Code Block */}
          {formData.blockType === 'code' && (
            <>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">Language</label>
                <select
                  name="image"
                  className="admin-crud-select"
                  value={formData.image || 'javascript'}
                  onChange={handleChange}
                >
                  <option value="C">C</option>
                  <option value="cpp">C++</option>
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="csharp">C#</option>
                  <option value="html">HTML</option>
                  <option value="css">CSS</option>
                  <option value="sql">SQL</option>
                </select>
              </div>
              <div className="admin-crud-form-group">
                <label className="admin-crud-label">Code</label>
                <textarea
                  name="text"
                  className="admin-crud-textarea"
                  rows="8"
                  value={formData.text}
                  onChange={handleChange}
                  placeholder="Paste your code here..."
                />
              </div>
            </>
          )}

          <div className="admin-crud-form-group">
            <label className="admin-crud-label">Display Order</label>
            <input
              type="number"
              name="order"
              className="admin-crud-input"
              value={formData.order}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="admin-crud-modal-footer" style={{ padding: '20px 0 0 0' }}>
            <button
              type="button"
              className="admin-crud-btn admin-crud-btn-secondary"
              onClick={() => {
                setShowFormModal(false);
                setSelectedBlock(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="admin-crud-btn admin-crud-btn-primary"
              onClick={handleFormSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : selectedBlock ? 'Update Block' : 'Add Block'}
            </button>
          </div>
        </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedBlock(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Content Block"
        message={`Are you sure you want to delete this ${selectedBlock?.blockType} block?`}
      />

      {/* Toast Notification */}
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </div>
  );
}

export default PostContentBlocks;